import { SxProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import React, { ReactNode, useCallback } from 'react';

import TabLabelWithAdornment from '~components/Tabs/TabLabelWithAdornment';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';

export interface TabProps<T> {
  dataUi?: string;
  disabled?: boolean;
  endAdornment?: ReactNode;
  label: string;
  value: T;
}

interface TabsProps<T> {
  onChange: (selected: T) => void;
  selected: T;
  sx?: SxProps;
  tabSx?: SxProps;
  tabs: TabProps<T>[];
}

export default function Tabs<T>({ onChange, selected, sx, tabSx, tabs }: TabsProps<T>) {
  const theme = useTheme();
  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: T) => {
      onChange(value);
    },
    [onChange],
  );

  return (
    <Box sx={sx}>
      <MuiTabs
        value={selected}
        onChange={handleChange}
        sx={{
          '&.MuiTabs-root': {
            minHeight: 0,
            '.MuiTabs-indicator': {
              display: 'none',
            },
            '.MuiTabs-flexContainer': {
              alignItems: 'center',
              position: 'relative',
              '&:before': {
                backgroundColor: theme.semantic.surfaceSunken,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: theme.brandV2.borderRadius,
                content: "''",
                display: 'block',
                inset: 1,
                position: 'absolute',
              },
            },
          },
        }}
      >
        {tabs.map((tab) => (
          <MuiTab
            key={String(tab.value)}
            disabled={tab.disabled}
            label={
              <TabLabelWithAdornment endAdornment={tab.endAdornment} label={tab.label} />
            }
            value={tab.value}
            {...(tab.dataUi && { 'data-ui': tab.dataUi })}
            sx={[
              {
                flex: 1,
                '&.MuiTab-root': {
                  borderLeft: 'solid 1px transparent',
                  borderRight: 'solid 1px transparent',
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '12px',
                  height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX - 2,
                  minHeight: 0,
                  opacity: 1,
                  p: theme.spacing(),
                  '&.Mui-selected': {
                    backgroundColor: theme.semantic.surfaceRaised,
                    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    borderRadius: theme.brandV2.borderRadius,
                    height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  },
                  '&.Mui-disabled': {
                    color: theme.palette.text.disabled,
                    opacity: 0.7,
                  },
                },
              },
              ...(Array.isArray(tabSx) ? tabSx : [tabSx]),
            ]}
          />
        ))}
      </MuiTabs>
    </Box>
  );
}
